import { isNullOrUndefined } from '@/utils/is-null-or-undefined';

import formatters from './formatters';
import transformers from './transformers';
import { SUPPORTED_UNITS } from './unit-formatter.constants';
import { FormattedUnitLabels, UnitValue } from './unit-formatter.types';
import { isPsigInhgMode, unitValueToFormattedUnitLabels } from './unit-formatter.utils';

/**
 * Normalizes unit value + display units before formatter selection.
 * Ensures composite pressure units (e.g. psig-inhg) resolve to the psig-inhg formatter key.
 */
export function resolveFormatUnitsInput(
  unitValue: UnitValue,
  displayUnits?: string
): { unitValue: UnitValue; displayUnits?: string } {
  const normalizedValue: UnitValue = {
    ...unitValue,
    units: unitValue.units === '' ? SUPPORTED_UNITS.NONE : unitValue.units,
  };

  if (isPsigInhgMode(normalizedValue, displayUnits)) {
    return {
      unitValue: normalizedValue,
      displayUnits: SUPPORTED_UNITS.PSIG_INHG,
    };
  }

  return { unitValue: normalizedValue, displayUnits };
}

/**
 * Formats a unit value to a formatted unit labels object.
 * @param unitValue - The unit value to format.
 * @param displayUnits - The display units to transform and format the unit value to.
 * @returns The formatted unit labels object.
 */
export const formatUnits = (unitValue: UnitValue, displayUnits?: string): FormattedUnitLabels => {
  const { unitValue: normalizedUnitValue, displayUnits: resolvedDisplayUnits } =
    resolveFormatUnitsInput(unitValue, displayUnits);

  const formatterKey =
    resolvedDisplayUnits?.toLowerCase() ?? normalizedUnitValue.units?.toLowerCase() ?? '';
  const formatter = formatters[formatterKey];
  if (formatter) {
    return formatter(normalizedUnitValue, resolvedDisplayUnits);
  }

  const outputUnitValue = transformUnits(normalizedUnitValue, resolvedDisplayUnits);

  return unitValueToFormattedUnitLabels({
    value: outputUnitValue.value,
    units: outputUnitValue.units,
  });
};

/**
 * Transforms a unit value to a new unit value.
 * @param unitValue - The unit value to transform.
 * @param newUnits - The new units to transform the unit value to.
 * @returns The transformed unit value.
 */
export const transformUnits = (unitValue: UnitValue, newUnits?: string): UnitValue => {
  // If the units is undefined, empty, set units to 'none'
  // This is to ensure that the none transformer is run
  if (isNullOrUndefined(unitValue.units) || unitValue.units === '') {
    unitValue.units = 'none';
  }

  const transformer = transformers[unitValue.units?.toLowerCase() ?? ''];

  if (transformer) {
    return transformer(unitValue, newUnits);
  }

  return unitValue;
};
